import classNames from 'classnames'
import { Loader2 } from 'lucide-react'
import React from 'react'
import { Button } from './button'
import { Dialog } from './dialog'

export type BulkActionDialogWidth = 'compact' | 'default' | 'wide'

export interface BulkActionDialogProps {
    isOpen: boolean
    title: React.ReactNode
    description?: React.ReactNode
    children: React.ReactNode
    footer?: React.ReactNode
    onClose: () => void
    width?: BulkActionDialogWidth
    /** Prevents Escape, backdrop, and close-button dismissal while a request cannot be cancelled. */
    dismissible?: boolean
    className?: string
}

const dialogWidths: Record<BulkActionDialogWidth, number> = {
    compact: 440,
    default: 560,
    wide: 880,
}

/**
 * Endpoint-agnostic shell for the configure -> running -> result bulk-action flow.
 *
 * The underlying shared Dialog supplies Radix focus management, accessible naming,
 * scroll locking, Escape handling, and focus return. This component owns only the
 * bulk-action visual grammar and is intentionally suitable for synchronous and
 * asynchronous flows alike.
 */
export function BulkActionDialog({
    isOpen,
    title,
    description,
    children,
    footer,
    onClose,
    width = 'default',
    dismissible = true,
    className,
}: BulkActionDialogProps) {
    const body = (
        <>
            {description && (
                <p className="mt-0 mb-[20px] text-[14px] leading-[20px] text-[#5f6b7c]">
                    {description}
                </p>
            )}
            {children}
        </>
    )

    return (
        <Dialog
            isOpen={isOpen}
            onClose={dismissible ? onClose : undefined}
            title={title}
            hideCloseButton={!dismissible}
            description={typeof description === 'string' ? description : undefined}
            canEscapeKeyClose={dismissible}
            canOutsideClickClose={dismissible}
            className={classNames('bg-white overflow-hidden', className)}
            style={{ width: 'calc(100vw - 32px)', maxWidth: dialogWidths[width] }}
        >
            <div className="min-h-0 flex-1 overflow-y-auto px-[24px] py-[22px]">{body}</div>
            {footer && (
                <div className="flex-none border-t border-[#d8e0e8] bg-white px-[24px] py-[16px]">
                    {footer}
                </div>
            )}
        </Dialog>
    )
}

export type BulkActionSelection =
    | { kind: 'selected'; count: number }
    | { kind: 'allMatching'; count: number }

export interface BulkActionSelectionSummaryProps {
    selection: BulkActionSelection
    /** Singular entity name, for example `campaign` or `keyword`. */
    entity: string
    /** Optional irregular plural. Defaults to `${entity}s`. */
    entityPlural?: string
    className?: string
}

/**
 * The scope line is the most consequential fact in a bulk-action dialog, so it
 * reads as a plain sentence rather than a bordered callout.
 */
export function BulkActionSelectionSummary({
    selection,
    entity,
    entityPlural = `${entity}s`,
    className,
}: BulkActionSelectionSummaryProps) {
    const label = selection.count === 1 ? entity : entityPlural
    const count = selection.count.toLocaleString()

    return (
        <p
            className={classNames(
                'm-0 mb-[20px] last:mb-0',
                // Regular weight: a bold line here competes with the field label
                // directly below it, and the pair reads as two headings.
                'text-[14px] leading-[20px] tabular-nums text-[#1c2127]',
                className,
            )}
        >
            {selection.kind === 'allMatching'
                ? `All ${count} matching ${label}`
                : `${count} ${label} selected`}
        </p>
    )
}

export interface BulkActionFieldProps {
    label: React.ReactNode
    htmlFor?: string
    description?: React.ReactNode
    /** Blocks submission. Rendered in the error tone and announced. */
    error?: React.ReactNode
    /**
     * Does NOT block submission — for example, entries that will be discarded.
     * Kept separate from `error` so a non-blocking notice never borrows the
     * error tone, and so it never displaces the format instructions.
     */
    warning?: React.ReactNode
    required?: boolean
    children: React.ReactNode
    className?: string
}

export function BulkActionField({
    label,
    htmlFor,
    description,
    error,
    warning,
    required,
    children,
    className,
}: BulkActionFieldProps) {
    return (
        <div className={classNames('mb-[18px] last:mb-0', className)}>
            <label
                htmlFor={htmlFor}
                className="mb-[7px] block text-[14px] font-semibold leading-[20px] text-[#1c2127]"
            >
                {label}
                {required && (
                    <span className="ml-[4px] text-[#c23030]" aria-hidden="true">
                        *
                    </span>
                )}
            </label>
            {children}
            {description && (
                <p className="m-0 mt-[6px] text-[13px] leading-[18px] text-[#738091]">
                    {description}
                </p>
            )}
            {error && (
                <p role="alert" className="m-0 mt-[6px] text-[13px] leading-[18px] text-[#c23030]">
                    {error}
                </p>
            )}
            {warning && !error && (
                <p className="m-0 mt-[6px] text-[13px] leading-[18px] text-[#8a4f0f]">{warning}</p>
            )}
        </div>
    )
}

export interface BulkActionDialogActionsProps {
    primaryLabel: string
    onPrimary?: () => void
    onCancel: () => void
    cancelLabel?: string
    loading?: boolean
    loadingLabel?: string
    disabled?: boolean
    destructive?: boolean
}

const actionRowClassName =
    'flex flex-col-reverse items-stretch gap-[10px] sm:flex-row sm:items-center sm:justify-end'

/**
 * Configure-step actions. `loading` means the submit request is in flight and
 * cannot be cancelled.
 *
 * The label stays visible next to the spinner. The shared Button's own `loading`
 * prop hides its text behind `visibility: hidden`, which also strips the button's
 * accessible name for the whole request.
 */
export function BulkActionDialogActions({
    primaryLabel,
    onPrimary,
    onCancel,
    cancelLabel = 'Cancel',
    loading = false,
    loadingLabel,
    disabled = false,
    destructive = false,
}: BulkActionDialogActionsProps) {
    return (
        <div className={actionRowClassName}>
            <Button text={cancelLabel} onClick={onCancel} disabled={loading} />
            <Button
                text={loading ? (loadingLabel ?? primaryLabel) : primaryLabel}
                icon={
                    loading ? (
                        <Loader2 aria-hidden="true" className="h-[14px] w-[14px] animate-spin" />
                    ) : undefined
                }
                onClick={onPrimary}
                disabled={disabled || loading}
                intent={destructive ? 'danger' : 'primary'}
            />
        </div>
    )
}

export interface BulkActionDialogResultActionsProps {
    /** Defaults to Done; use Close when dismissal is the clearest wording. */
    label?: string
    onDismiss: () => void
}

/** A single, unambiguous dismissal action for terminal result views. */
export function BulkActionDialogResultActions({
    label = 'Done',
    onDismiss,
}: BulkActionDialogResultActionsProps) {
    return (
        <div className="flex justify-end">
            <Button text={label} intent="primary" onClick={onDismiss} />
        </div>
    )
}

export const bulkActionInputClassName = classNames(
    'block w-full rounded-[4px] border border-[#a7b6c2] bg-white px-[11px] py-[8px]',
    'text-[14px] leading-[20px] text-[#1c2127] shadow-[inset_0_1px_2px_rgba(17,20,24,0.08)]',
    'placeholder:text-[#8f99a8] focus:border-[#2d72d2] focus:outline-none focus:ring-2 focus:ring-[rgba(45,114,210,0.2)]',
    'disabled:cursor-not-allowed disabled:bg-[#edeff2] disabled:text-[#8f99a8]',
)
